/**
 * Sysadmin Runtime → Queues (Phase 5 PR-D v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.6 + Phase 5 architect §2.4:
 *   Queues sub-tab surfaces BullMQ queue depths so operators spot
 *   stuck jobs / backlogs / failure spikes. v1 is read-only — pause /
 *   drain / requeue actions are v2 and live behind a per-queue detail
 *   drawer (deferred).
 *
 * Refresh: 5s via createSysadminQuery (overridden in
 * use-sysadmin-runtime-ops.ts) — queues move fast enough that the
 * default 10s feels stale during an active ingestion burst.
 */

import { createFileRoute } from '@tanstack/react-router';
import {
  Layers,
  RefreshCw,
  AlertTriangle,
  Clock,
  PauseCircle,
  PlayCircle,
  CheckCircle2,
  XCircle,
  Hourglass,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatTile, type StatTileTone } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { PageError } from '@/components/ui/page-error';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useSysadminRuntimeQueues,
  type QueueRow,
} from '@/lib/use-sysadmin-runtime-ops';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/runtime/queues')({
  component: QueuesPage,
});

function QueuesPage() {
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } =
    useSysadminRuntimeQueues();

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load queue stats"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      </div>
    );
  }

  const queues = data?.queues ?? [];
  const totals = queues.reduce(
    (acc, q) => ({
      waiting: acc.waiting + q.waiting,
      active: acc.active + q.active,
      failed: acc.failed + q.failed,
      delayed: acc.delayed + q.delayed,
    }),
    { waiting: 0, active: 0, failed: 0, delayed: 0 },
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Layers className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Background Queues
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.6</Badge>
              <Badge variant="secondary" className="text-xs">PR-D</Badge>
              {dataUpdatedAt > 0 && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')}
          />
          Refresh
        </Button>
      </div>

      {/* ── Totals strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Waiting"
          value={totals.waiting.toLocaleString()}
          icon={Hourglass}
          tone={totals.waiting > 100 ? 'warning' : 'default'}
          hint="Across all queues"
        />
        <StatTile
          label="Active"
          value={totals.active.toLocaleString()}
          icon={PlayCircle}
          tone={totals.active > 0 ? 'success' : 'muted'}
          hint="Currently processing"
        />
        <StatTile
          label="Failed"
          value={totals.failed.toLocaleString()}
          icon={XCircle}
          tone={totals.failed > 0 ? 'destructive' : 'success'}
          hint="Lifetime failed jobs"
        />
        <StatTile
          label="Delayed"
          value={totals.delayed.toLocaleString()}
          icon={Clock}
          tone={totals.delayed > 0 ? 'warning' : 'muted'}
          hint="Scheduled-future jobs"
        />
      </div>

      {/* ── Per-queue grid ── */}
      {isLoading && queues.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
          Probing queues…
        </div>
      ) : queues.length === 0 ? (
        <EmptyState
          icon={Layers}
          title="No queues reachable"
          body="The queue probe returned no rows. Either no queues are registered or Valkey is down (check Runtime → Health)."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {queues.map((q) => (
            <QueueTile key={q.name} queue={q} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Atoms ───────────────────────────────────────────────────────────────

function QueueTile({ queue }: { queue: QueueRow }) {
  const hasError = queue.probeError != null;
  const paused = queue.paused > 0;
  const failing = queue.failed > 0;

  const tone: StatTileTone = hasError
    ? 'destructive'
    : paused
      ? 'warning'
      : failing
        ? 'warning'
        : 'default';

  return (
    <div
      className={cn(
        'rounded-md border p-3 space-y-2.5',
        tone === 'destructive' && 'border-destructive/30 bg-destructive/5',
        tone === 'warning' && 'border-amber-500/30 bg-amber-500/5',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm truncate" title={queue.name}>
            {queue.name}
          </div>
        </div>
        {paused ? (
          <StatusPill status="warning" compact icon={PauseCircle}>Paused</StatusPill>
        ) : hasError ? (
          <StatusPill status="destructive" compact icon={AlertTriangle}>Probe error</StatusPill>
        ) : queue.active > 0 ? (
          <StatusPill status="info" compact icon={PlayCircle}>Active</StatusPill>
        ) : (
          <StatusPill status="muted" compact>Idle</StatusPill>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <Counter label="Waiting" value={queue.waiting} tone={queue.waiting > 100 ? 'warning' : 'default'} icon={Hourglass} />
        <Counter label="Active"  value={queue.active}  tone={queue.active > 0 ? 'success' : 'muted'} icon={PlayCircle} />
        <Counter label="Delayed" value={queue.delayed} tone={queue.delayed > 0 ? 'warning' : 'muted'} icon={Clock} />
        <Counter label="Completed" value={queue.completed} tone="muted" icon={CheckCircle2} />
        <Counter label="Failed"  value={queue.failed}  tone={queue.failed > 0 ? 'destructive' : 'muted'} icon={XCircle} />
        <Counter label="Paused"  value={queue.paused}  tone={queue.paused > 0 ? 'warning' : 'muted'} icon={PauseCircle} />
      </div>

      {queue.probeError && (
        <div className="text-[11px] text-destructive bg-destructive/10 rounded p-2">
          {queue.probeError}
        </div>
      )}
    </div>
  );
}

function Counter({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: number;
  tone: 'default' | 'success' | 'warning' | 'destructive' | 'muted';
  icon: typeof PlayCircle;
}) {
  const toneClass =
    tone === 'success'
      ? 'text-emerald-600'
      : tone === 'warning'
        ? 'text-amber-600'
        : tone === 'destructive'
          ? 'text-destructive'
          : tone === 'muted'
            ? 'text-muted-foreground'
            : '';
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1">
        <Icon className="h-2.5 w-2.5" />
        {label}
      </div>
      <div className={cn('font-mono font-semibold tabular-nums text-sm', toneClass)}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}
